/* Home, category and editor screens. Markup and behaviour follow the approved prototype; data goes through the store. */
import * as L from "../domain/library";
import { esc, normalizeUrl, nowIso } from "../domain/format";
import { newId } from "../domain/ids";
import { CATEGORY_LAYOUTS, DEFAULT_LAYOUT, type Action, type Category, type CategoryLayout, type Workspace } from "../domain/types";
import { ctx } from "./context";
import { COLORS, col, glassIcon, ic } from "./icons";
import { iconPicker } from "./icon-picker";
import { layoutHTML, layoutName, layoutThumb, sampleActions } from "./layouts";
import { closeSheet, copyText, datesHTML, head, openSheet, toast } from "./shell";
import { SAVE_FAILED_MESSAGE } from "../data/store";
import { renderPlans, renderSettings, handleSettingsAction } from "./settings";
import { renderWelcome, handleWelcomeAction } from "./welcome";
import { bannersHTML, handleAccountAction, renderAccountView } from "./account";
import { failNotice, showFail } from "./failure";
import folderRear from "./figma/folder-rear.svg";
import folderFront from "./figma/folder-front.svg";

const $app = () => document.getElementById("app")!;
const lib = () => ctx.store.lib;
const V = ctx.V;
const space = () => L.activeWorkspace(lib());
const cat = () => {
  const c = V.catId ? L.getCategory(lib(), V.catId) : undefined;
  const s = c && L.getWorkspace(lib(), c.workspaceId);
  return c && s ? { s, c } : null;
};
const findAction = (id: string) => {
  const a = L.getAction(lib(), id); if (!a) return null;
  const c = L.getCategory(lib(), a.categoryId)!; const s = L.getWorkspace(lib(), c.workspaceId)!;
  return { s, c, a };
};
/** Saves a change; on failure nothing in the app changes and the caller keeps its form open. */
const save = (mutate: Parameters<typeof ctx.store.commit>[0]) => ctx.store.commit(mutate);
/** inline error inside a sheet, hidden until a save fails */
const saveErrHTML = (id: string) => `<p class="err" id="${id}" role="alert" hidden>${SAVE_FAILED_MESSAGE}</p>`;
/** runs one save at a time per button, so a double tap cannot add twice */
async function guarded(btn: HTMLElement, run: () => Promise<void>) {
  if (btn.dataset.busy) return;
  btn.dataset.busy = "1";
  try { await run(); } finally { delete btn.dataset.busy; }
}

/* ---------- render ---------- */
const BRAND_VIEWS = ["home", "welcome", "login", "loading", "load-error"];
export const brandHTML = () => `<div class="brand"><span class="mark"><i></i><i></i><i></i><i></i></span><span class="wordmark">MEMOLINK</span><span class="proto">BETA</span></div>`;
export function topBar() {
  const left = BRAND_VIEWS.includes(V.view)
    ? brandHTML()
    : V.view === "plans"
    ? `<div class="brand"><button class="back" data-do="settings" id="btn-back">${ic("left")}<span>ตั้งค่า</span></button></div>`
    : `<div class="brand"><button class="back" data-do="back" id="btn-back">${ic("left")}<span>${esc(V.view === "settings" ? "กลับ" : (cat()?.s.name || "กลับ"))}</span></button></div>`;
  // home: icon-only square button (Figma 11:1124); category: icon + label (Figma 5:143)
  const editBtn = V.view === "home"
    ? `<button class="iconbtn sq ${V.edit ? "on" : ""}" data-do="toggle-edit" id="btn-edit" aria-pressed="${V.edit}" aria-label="${V.edit ? "เสร็จ" : "แก้ไข"}">${ic(V.edit ? "check" : "pencil")}</button>`
    : V.view === "cat"
    ? `<button class="iconbtn lbl ${V.edit ? "on" : ""}" data-do="toggle-edit" id="btn-edit" aria-pressed="${V.edit}">${V.edit ? ic("check") + "เสร็จ" : ic("pencil") + "แก้ไข"}</button>`
    : "";
  const setBtn = V.view === "home" ? `<button class="iconbtn sq" data-do="settings" id="btn-settings" aria-label="ตั้งค่า">${ic("sliders")}</button>` : "";
  return `<header class="top">${left}${editBtn}${setBtn}</header>`;
}
const strip = () => V.edit ? `<div class="edit-strip">${ic("pencil")}<span>โหมดแก้ไข แตะรายการเพื่อแก้ชื่อ สี ไอคอน หรือลิงก์</span></div>` : "";
const pen = () => V.edit ? `<span class="pen">${ic("pencil")}</span>` : "";

/**
 * Compact grids are a per-device choice, not part of the library.
 *  - category folders on home: 3 columns by default, 2 on request (own key);
 *  - Actions in a standard category: 4 columns on request, 3 by default (the original key).
 * Both used one key before; someone who had already picked a folder grid there keeps that choice.
 */
const DENSE_KEY = "memolink-grid-dense";
const FOLDER_COLS_KEY = "memolink-folder-cols";
const readKey = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
let dense = readKey(DENSE_KEY) === "1";
let folders3 = (() => { const v = readKey(FOLDER_COLS_KEY); if (v === "2" || v === "3") return v === "3"; const old = readKey(DENSE_KEY); return old === null ? true : old === "1"; })();
/** Settings › display: the same per-device choices as the grid buttons on home and in a category */
export const gridPrefs = () => ({ folders3, dense });
export function setGridPref(which: "folders" | "actions", on: boolean) {
  if (which === "folders") folders3 = on; else dense = on;
  try { localStorage.setItem(which === "folders" ? FOLDER_COLS_KEY : DENSE_KEY, which === "folders" ? (on ? "3" : "2") : (on ? "1" : "0")); } catch { /* only this session */ }
}
const gridToggle = (on = dense) => `<button class="grid-toggle" data-do="grid-toggle" id="btn-grid" aria-pressed="${on}" aria-label="${on ? "แสดงกริดขนาดปกติ" : "แสดงกริดแบบกะทัดรัด"}">${ic("grid")}</button>`;

/** Category folder from Figma (01 • Workspace / Categories — Redesign): rear leaf + dark satin front, icon in the category colour */
export function folderHTML(c: { glow: string; icon: string }) {
  return `<span class="folder" style="--g:${col(c.glow)}"><img src="${folderRear}" alt="" draggable="false"><img src="${folderFront}" alt="" draggable="false">${ic(c.icon)}</span>`;
}
function glassHTML(a: { icon: string; color: string; type: string }) {
  return `<span class="iconwrap">${glassIcon(a.icon, col(a.color))}${a.type === "memo" ? `<span class="memo-dot">${ic("lines")}</span>` : ""}</span>`;
}

function renderHome() {
  const sp = space();
  const tabs = L.workspacesOf(lib()).map(s => {
    const on = s.id === sp?.id;
    return `<button class="tab" role="tab" aria-selected="${on}" data-do="tab" data-id="${s.id}">${on ? `<span class="tab-ic">${ic(s.icon)}</span>` : ic(s.icon)}<span class="tab-name">${esc(s.name)}</span></button>`;
  }).join("");
  const cats = sp ? L.categoriesOf(lib(), sp.id) : [];
  const cells = sp
    ? cats.map(c => `<button class="cell" data-do="cat" data-id="${c.id}" data-press="cat">${pen()}${folderHTML(c)}<span class="name">${esc(c.name)}</span></button>`).join("")
      + `<button class="add-row" data-do="new-cat" id="btn-add-cat">${ic("plus")}<span>เพิ่มหมวด</span></button>`
    : `<div class="empty">ยังไม่มีสถานที่ แตะ “+” เพื่อเพิ่มสถานที่แรก</div>`;
  $app().innerHTML = topBar()
    + `<div class="ws-row"><nav class="tabs ${V.edit ? "editing" : ""}" role="tablist" aria-label="สถานที่">${tabs}</nav>`
    + `<button class="ws-add" data-do="new-space" id="btn-add-space" aria-label="เพิ่มสถานที่">${ic("plus")}</button></div>`
    + strip()
    + (sp ? `<div class="sec-head"><h1>หมวดใน ${esc(sp.name)}</h1><span>${cats.length} หมวด</span></div>` : "")
    + `<div class="grid folders ${folders3 ? "dense" : ""}">${cells}</div>`
    + (sp ? `<div class="grid-foot">${gridToggle(folders3)}</div>` : "")
    + `<p class="hint">${V.edit ? "ในโหมดแก้ไข แตะแท็บสถานที่ที่เลือกอยู่เพื่อแก้ชื่อหรือไอคอน" : cats.length ? "แตะค้างที่หมวดเพื่อดูรายละเอียดและวันที่" : "แตะ “เพิ่มหมวด” เพื่อสร้างหมวดแรก"}</p>`;
  const sel = $app().querySelector('.tab[aria-selected="true"]');
  if (sel) sel.scrollIntoView({ block: "nearest", inline: "nearest" });
}

/**
 * The Actions of a category as its page draws them: the original grid for "standard" (markup unchanged), the category
 * type's layout otherwise. The category editor's live preview draws sample Actions with this same function.
 */
function actionsArea(layout: CategoryLayout, acts: Action[], o: { edit: boolean; pen: string }, empty: string, withAdd = true) {
  if (layout !== "standard") return layoutHTML(layout, acts, o, empty, withAdd);
  const cells = acts.map(a => {
    const sub = a.type === "link" && !a.url ? `<span class="sub warn">ยังไม่มีลิงก์</span>` : "";
    const inner = `${o.pen}${glassHTML(a)}<span class="name">${esc(a.name)}</span>${sub}`;
    const share = !o.edit && a.type === "link" ? `<button class="share ${a.url ? "" : "none"}" data-do="share" data-id="${a.id}" aria-label="แชร์ ${esc(a.name)}">${ic("share")}</button>` : "";
    const main = (!o.edit && a.type === "link" && a.url)
      ? `<a class="cell act" href="${esc(a.url)}" target="_blank" rel="noopener noreferrer" data-do="open-link" data-id="${a.id}" data-press="act">${inner}</a>`
      : `<button class="cell act" data-do="act" data-id="${a.id}" data-press="act">${inner}</button>`;
    return `<div class="actcell">${main}${share}</div>`;
  }).join("");
  const add = withAdd ? `<button class="cell act" data-do="new-act" id="btn-add-act"><span class="add-glass">${ic("plus")}</span><span class="name addl">เพิ่ม Action</span></button>` : "";
  return `<div class="grid ${dense ? "dense" : ""}">${empty}${cells}${add}</div>`;
}

/** scales the preview page down to the preview box, so it keeps the page's real width and proportions */
function fitPreview(sh: HTMLElement) {
  const box = sh.querySelector<HTMLElement>(".lp-screen"), page = sh.querySelector<HTMLElement>(".lp-page");
  if (!box || !page) return;
  page.style.zoom = String(Math.min(1, (box.clientWidth - 16) / parseFloat(page.style.width)));
}

/**
 * Live preview at the top of the category editor: the category page as it will look after saving, drawn by the same
 * code as the page, with sample Actions in the chosen colour. View only: it is inert and nothing in it is saved.
 */
function catPreviewHTML(d: { name: string; icon: string; glow: string; layout: CategoryLayout }) {
  const w = Math.max(280, Math.min(408, $app().clientWidth - 32)); // the width the category page draws its Actions at
  return `<div class="lp" id="ed-preview" data-layout="${d.layout}" aria-label="ตัวอย่างหน้าหมวด">`
    + `<div class="lp-side">${folderHTML({ glow: d.glow, icon: d.icon })}<b class="lp-name">${esc(d.name || "ชื่อหมวด")}</b>`
    + `<small class="lp-type">${esc(layoutName(d.layout))}</small><span class="lp-tag">ข้อมูลตัวอย่าง</span></div>`
    + `<div class="lp-screen" inert aria-hidden="true"><div class="lp-page" style="width:${w}px;zoom:.6">`
    + actionsArea(d.layout, sampleActions(d.layout, d.glow), { edit: false, pen: "" }, "", false)
    + `</div></div></div>`;
}

function renderCat() {
  const r = cat(); if (!r) { V.view = "home"; return render(); }
  const { s, c } = r;
  const acts = L.actionsOf(lib(), c.id);
  const empty = acts.length ? "" : `<div class="empty">หมวดนี้ยังว่าง แตะ “เพิ่ม Action” เพื่อเพิ่มลิงก์หรือ Memo</div>`;
  // "standard" keeps the original grid markup exactly; every other category type has its own layout (ui/layouts.ts)
  const std = c.layout === DEFAULT_LAYOUT;
  $app().innerHTML = topBar() + strip()
    + `<div class="cat-hero">${folderHTML(c)}<div><div class="crumb">${esc(s.name)} › หมวด${std ? "" : ` · <span id="cat-layout">${esc(layoutName(c.layout))}</span>`}</div><h2>${esc(c.name)}</h2><div class="crumb">${acts.length} รายการ</div></div></div>`
    + `<div class="sec-head acts"><h1>Actions</h1><span>${acts.length} รายการ</span>${std ? gridToggle() : ""}</div>`
    + actionsArea(c.layout, acts, { edit: V.edit, pen: pen() }, empty)
    + `<p class="hint">${V.edit ? "แตะ Action เพื่อแก้ไข" : std ? "แตะไอคอนลิงก์เพื่อเปิดทันที · ปุ่มมุมขวาบนสำหรับแชร์ · แตะ Memo เพื่ออ่านและคัดลอก · แตะค้างเพื่อดูรายละเอียด"
      : "แตะลิงก์เพื่อเปิดทันที · ปุ่มแชร์อยู่มุมขวา · แตะ Memo เพื่ออ่านและคัดลอก · แตะค้างเพื่อดูรายละเอียด"}</p>`;
}

export function render() {
  if (V.view === "loading" || V.view === "login" || V.view === "load-error") renderAccountView($app(), V.view);
  else if (V.view === "welcome") renderWelcome($app());
  else if (V.view === "settings") renderSettings($app());
  else if (V.view === "plans") renderPlans($app());
  else if (V.view === "cat") renderCat();
  else renderHome();
  const banners = bannersHTML();
  if (banners) $app().querySelector("header.top")?.insertAdjacentHTML("afterend", banners);
}
ctx.render = render;

/* ---------- editor for space / category / action, new or existing ---------- */
type Kind = "space" | "cat" | "act";
/** used when the editor is reopened after "load latest": the person's own edits and a note on what happened */
interface EditorOpts { draft?: Record<string, unknown>; touched?: Set<string>; notice?: string; parentId?: string }
const KIND_ENTITY = { space: "workspace", cat: "category", act: "action" } as const;
const getRec = (kind: Kind, id: string) => kind === "space" ? L.getWorkspace(lib(), id) : kind === "cat" ? L.getCategory(lib(), id) : L.getAction(lib(), id);
function openEditor(kind: "space", existing?: Workspace, opts?: EditorOpts): void;
function openEditor(kind: "cat", existing?: Category, opts?: EditorOpts): void;
function openEditor(kind: "act", existing?: Action, opts?: EditorOpts): void;
function openEditor(kind: Kind, existing?: Workspace | Category | Action, opts: EditorOpts = {}) {
  const isNew = !existing;
  const d: any = { ...(existing ? { ...existing } :
    kind === "space" ? { name: "", icon: "home" } :
    kind === "cat" ? { name: "", icon: "folder", glow: "violet", layout: DEFAULT_LAYOUT } :
    { type: "link", name: "", icon: "link", color: "violet", url: "", text: "" }), ...(opts.draft ?? {}) };
  /** fields the person changed in this form; after "load latest" only these keep the typed value */
  const touched = opts.touched ?? new Set<string>();
  // a new record gets its id and creation time once, so saving again after a failed or lost save cannot add it twice
  if (isNew) { d.id ??= newId(KIND_ENTITY[kind]); d.at ??= d.createdAt ?? nowIso(); }
  const parentId: string | undefined = opts.parentId ?? (kind === "cat" ? (existing as Category | undefined)?.workspaceId ?? space()?.id
    : kind === "act" ? (existing as Action | undefined)?.categoryId ?? cat()?.c.id : undefined);
  const parentGone = isNew && kind !== "space" && !(kind === "cat" ? L.getWorkspace(lib(), parentId ?? "") : L.getCategory(lib(), parentId ?? ""));
  const title = (isNew ? "เพิ่ม" : "แก้ไข") + ({ space: "สถานที่", cat: "หมวด", act: " Action" }[kind]);
  const colorKey = kind === "cat" ? "glow" : "color";
  const colorLabel = kind === "cat" ? "สีแสงกรอบ" : "สีไอคอน";
  d.layout ??= DEFAULT_LAYOUT; // categories saved before category types
  const drawIcon = (ref: string) => kind === "act" ? glassIcon(ref, col(d.color)) : ic(ref);
  /** icon group that matches a category type ("standard" ↔ "other"); the picker opens there when the type changes */
  const typeGroup = (l: string) => l === DEFAULT_LAYOUT ? "other" : l;
  const picker = iconPicker(d.icon, drawIcon, ref => { d.icon = ref; touched.add("icon"); refresh(); }, kind === "act", !isNew || !!opts.draft,
    kind === "cat" ? typeGroup(d.layout) : undefined);
  let refresh = () => {};

  const body = () => {
    if (kind === "cat") return catPreviewHTML(d);
    const prev = kind === "space"
      ? `<span class="tab" aria-selected="true" style="pointer-events:none">${ic(d.icon)}${esc(d.name || "ชื่อสถานที่")}</span>`
      : `<span class="cell act">${glassHTML(d)}<span class="name">${esc(d.name || "ชื่อ Action")}</span></span>`;
    return `<div class="preview" id="ed-preview">${prev}</div>`;
  };
  const notice = opts.notice ? `<p class="note" id="ed-notice"><span class="tag">โหลดล่าสุดแล้ว</span> ${esc(opts.notice)}</p>` : "";
  const html = (kind === "cat" ? `<div class="lp-sticky">${head(title)}${body()}</div>${notice}` : head(title) + notice + body())
    + (kind === "act" ? `<div class="field"><span class="lab">ประเภท</span><div class="seg" id="ed-type">
         <button type="button" data-t="link" aria-pressed="${d.type === "link"}">${ic("link")}ลิงก์</button>
         <button type="button" data-t="memo" aria-pressed="${d.type === "memo"}">${ic("note")}Memo</button></div></div>` : "")
    + `<div class="field"><label for="ed-name">ชื่อ</label><input class="inp" id="ed-name" maxlength="28" value="${esc(d.name)}" placeholder="${kind === "act" ? "เช่น Sheet คำนวณ Breakeven" : kind === "cat" ? "เช่น ลิงก์สำคัญ" : "เช่น Office"}" autocomplete="off"><small>สั้นไว้จะอ่านง่ายในกริด 3 คอลัมน์ (แสดงได้ 2 บรรทัด)</small><span class="err" id="ed-name-err" hidden>กรุณาใส่ชื่อ</span></div>`
    + (kind === "act" ? `<div class="field" id="ed-url-f" ${d.type === "memo" ? "hidden" : ""}><label for="ed-url">ลิงก์ปลายทาง</label><input class="inp" id="ed-url" inputmode="url" value="${esc(d.url)}" placeholder="วางลิงก์ เช่น https://..." autocomplete="off"><small>เว้นว่างได้ จะแสดงเป็น “ยังไม่มีลิงก์”</small><span class="err" id="ed-url-err" hidden>ลิงก์ไม่ถูกต้อง ต้องเป็น http:// หรือ https://</span></div>
         <div class="field" id="ed-text-f" ${d.type === "link" ? "hidden" : ""}><label for="ed-text">ข้อความ Memo</label><textarea class="inp" id="ed-text" placeholder="พิมพ์ข้อความที่ต้องการคัดลอกบ่อย">${esc(d.text)}</textarea></div>` : "")
    + (kind === "cat" ? `<div class="field"><span class="lab" id="ed-layout-lab">ประเภท</span><div class="layouts" id="ed-layout" role="radiogroup" aria-labelledby="ed-layout-lab">${CATEGORY_LAYOUTS.map(l =>
        `<button type="button" role="radio" data-l="${l.key}" aria-checked="${d.layout === l.key}">${layoutThumb(l.key)}<span class="lt"><b>${esc(l.name)}</b><small>${esc(l.hint)}${l.key === DEFAULT_LAYOUT ? " · ค่าเริ่มต้น" : ""}</small></span></button>`).join("")}</div>`
        + `<small>ตัวอย่างด้านบนคือหน้าตาหลังบันทึก ไอคอนเลือกจากกลุ่มไหนก็ได้</small></div>` : "")
    + `<div class="field"><span class="lab">ไอคอน</span>${picker.html}</div>`
    + (kind !== "space" ? `<div class="field"><span class="lab">${colorLabel}</span><div class="swatches" id="ed-colors">${COLORS.map(c => `<button type="button" class="sw" style="--s:${c.v}" data-c="${c.k}" aria-pressed="${d[colorKey] === c.k}"><i></i><span>${c.n}</span></button>`).join("")}</div>${kind === "act" ? `<small>เปลี่ยนเฉพาะสีไอคอนนี้ แยกจากสีหมวด</small>` : `<small>เปลี่ยนเฉพาะแสงกรอบของหมวดนี้ ไม่เปลี่ยนธีมแอป</small>`}</div>` : "")
    + (existing ? datesHTML(existing) : "")
    + saveErrHTML("ed-save-err")
    + `<div class="stack"><button class="btn primary" id="ed-save" ${parentGone ? "disabled" : ""}>${ic("check")}บันทึก</button>`
    + (isNew ? "" : `<button class="btn danger" id="ed-del">${ic("trash")}ลบ${kind === "space" ? "สถานที่นี้" : kind === "cat" ? "หมวดนี้" : " Action นี้"}</button>`)
    + `</div>`;

  openSheet(html, sh => {
    const q = <T extends HTMLElement = HTMLElement>(s: string) => sh.querySelector<T>(s);
    refresh = () => { q("#ed-preview")!.outerHTML = body(); fitPreview(sh); };
    fitPreview(sh);
    picker.mount(sh);
    q<HTMLInputElement>("#ed-name")!.addEventListener("input", e => { d.name = (e.target as HTMLInputElement).value; touched.add("name"); refresh(); });
    q("#ed-layout")?.addEventListener("click", e => { const b = (e.target as Element).closest<HTMLElement>("[data-l]"); if (!b) return; d.layout = b.dataset.l; touched.add("layout");
      sh.querySelectorAll("#ed-layout [data-l]").forEach(x => x.setAttribute("aria-checked", String(x === b)));
      picker.openGroup(typeGroup(d.layout)); refresh(); });
    // the live preview stays pinned while scrolling; while typing (phone keyboard open) it shrinks to one line so it
    // never covers the field being typed in. It grows back only after the tap that ended typing, so the tapped button
    // does not move away under the finger, and the scroll position is kept so the form does not jump.
    if (kind === "cat") {
      sh.classList.add("has-lp");
      const setTyping = (on: boolean) => {
        if (sh.classList.contains("typing") === on) return;
        const h = q("#ed-preview")!.offsetHeight;
        sh.classList.toggle("typing", on);
        if (sh.scrollTop > 0) sh.scrollTop += q("#ed-preview")!.offsetHeight - h;
      };
      const typingIn = () => !!document.activeElement?.matches(".sheet input, .sheet textarea");
      let pressed = false;
      sh.addEventListener("pointerdown", () => { pressed = true; }, true);
      sh.addEventListener("focusin", e => { if ((e.target as Element).matches("input, textarea")) setTyping(true); });
      sh.addEventListener("focusout", () => {
        const done = () => setTimeout(() => { if (!typingIn()) setTyping(false); });
        if (pressed) document.addEventListener("pointerup", () => { pressed = false; done(); }, { once: true }); else done();
      });
    }
    q("#ed-colors")?.addEventListener("click", e => { const b = (e.target as Element).closest<HTMLElement>("[data-c]"); if (!b) return; d[colorKey] = b.dataset.c; touched.add(colorKey);
      sh.querySelectorAll("#ed-colors button").forEach(x => x.setAttribute("aria-pressed", String(x === b)));
      if (kind === "act") picker.redraw();
      refresh(); });
    q("#ed-type")?.addEventListener("click", e => { const b = (e.target as Element).closest<HTMLElement>("[data-t]"); if (!b) return; d.type = b.dataset.t; touched.add("type");
      sh.querySelectorAll("#ed-type button").forEach(x => x.setAttribute("aria-pressed", String(x === b)));
      q("#ed-url-f")!.hidden = d.type !== "link"; q("#ed-text-f")!.hidden = d.type !== "memo";
      if (isNew && (d.icon === "link" || d.icon === "note")) { d.icon = d.type === "memo" ? "note" : "link"; picker.select(d.icon); }
      refresh(); });
    q("#ed-url")?.addEventListener("input", e => { d.url = (e.target as HTMLInputElement).value; touched.add("url"); q("#ed-url-err")!.hidden = true; });
    q("#ed-text")?.addEventListener("input", e => { d.text = (e.target as HTMLTextAreaElement).value; touched.add("text"); });

    /** after "load latest": reopen with the newest copy, keeping only the fields the person changed */
    const reopenLatest = () => {
      const mine: Record<string, unknown> = {};
      touched.forEach(k => { mine[k] = d[k]; });
      const latest = getRec(kind, existing?.id ?? d.id);
      if (latest) { openEditor(kind as any, latest as any, { draft: mine, touched, notice: "ช่องที่คุณแก้ยังเป็นค่าที่กรอกไว้ ช่องอื่นเป็นข้อมูลล่าสุด ตรวจแล้วกดบันทึกอีกครั้ง" }); return; }
      const parentOk = kind === "space" || !!(kind === "cat" ? L.getWorkspace(lib(), parentId ?? "") : L.getCategory(lib(), parentId ?? ""));
      openEditor(kind as any, undefined, { draft: { ...d, ...mine }, touched, parentId,
        notice: !existing ? "ค่าที่กรอกยังอยู่ กดบันทึกอีกครั้ง"
          : parentOk ? "รายการนี้ถูกลบจากอีกเครื่องแล้ว กดบันทึกเพื่อสร้างใหม่ด้วยค่าที่กรอกไว้"
          : "รายการนี้และที่อยู่ของมันถูกลบจากอีกเครื่องแล้ว บันทึกไม่ได้ คัดลอกข้อความที่กรอกเก็บไว้ได้" });
    };
    const saveErr = q("#ed-save-err")!;
    const saveBtn = q("#ed-save")!;
    saveBtn.addEventListener("click", () => guarded(saveBtn, async () => {
      saveErr.hidden = true;
      if (parentGone) return;
      d.name = String(d.name).trim();
      q("#ed-name-err")!.hidden = !!d.name; if (!d.name) { q("#ed-name")!.focus(); return; }
      if (kind === "act" && d.type === "link") { const n = normalizeUrl(d.url || ""); if (!n.ok) { q("#ed-url-err")!.hidden = false; q("#ed-url")!.focus(); return; } d.url = n.url; }
      const meta = { id: d.id, at: d.at };
      const r = await save(l => {
        // a new record that is already there was saved by an earlier try whose reply was lost: update it instead
        const id = existing?.id ?? (getRecIn(l, kind, d.id) ? d.id : null);
        if (kind === "space") {
          if (id) L.updateWorkspace(l, id, { name: d.name, icon: d.icon });
          else L.addWorkspace(l, { name: d.name, icon: d.icon }, meta);
        } else if (kind === "cat") {
          const input = { name: d.name, icon: d.icon, glow: d.glow, layout: d.layout };
          if (id) L.updateCategory(l, id, input); else L.addCategory(l, parentId!, input, meta);
        } else {
          const input = { type: d.type, name: d.name, icon: d.icon, color: d.color, url: d.url ?? "", text: d.text ?? "" };
          if (id) L.updateAction(l, id, input); else L.addAction(l, parentId!, input, meta);
        }
      });
      if (!r.ok) { showFail(saveErr, r, reopenLatest); return; } // keep the form and what was typed
      closeSheet(); render(); toast(isNew ? "เพิ่มแล้ว" : "บันทึกแล้ว");
    }));

    const del = q("#ed-del");
    if (del) del.addEventListener("click", () => guarded(del, async () => {
      const ex = existing!;
      if (!del.classList.contains("arm")) {
        del.classList.add("arm");
        const nCats = kind === "space" ? L.categoriesOf(lib(), ex.id).length : 0;
        const nActs = kind === "cat" ? L.actionsOf(lib(), ex.id).length : 0;
        del.innerHTML = ic("trash") + (nCats ? `แตะอีกครั้งเพื่อลบ (รวม ${nCats} หมวด)` : nActs ? `แตะอีกครั้งเพื่อลบ (รวม ${nActs} Action)` : "แตะอีกครั้งเพื่อยืนยันลบ");
        return;
      }
      if (kind === "space" && lib().workspaces.length === 1) { toast("ต้องมีอย่างน้อย 1 สถานที่"); return; }
      saveErr.hidden = true;
      const r = await save(l => {
        if (kind === "space") L.deleteWorkspace(l, ex.id);
        else if (kind === "cat") L.deleteCategory(l, ex.id);
        else L.deleteAction(l, ex.id);
      });
      if (!r.ok) { // nothing was deleted
        showFail(saveErr, r, () => {
          const latest = getRec(kind, ex.id);
          if (latest) openEditor(kind as any, latest as any, { notice: "โหลดข้อมูลล่าสุดแล้ว ตรวจแล้วกดลบอีกครั้งถ้ายังต้องการลบ" });
          else { closeSheet(); toast("รายการนี้ถูกลบจากอีกเครื่องแล้ว"); }
        });
        return;
      }
      if (kind === "cat" && V.catId === ex.id) { V.view = "home"; V.catId = null; }
      closeSheet(); render(); toast("ลบแล้ว");
    }));
  });
}

function openMemo(a: Action) {
  openSheet(head(a.name, `<span style="width:40px;height:40px;flex:none">${glassIcon(a.icon, col(a.color)).replace('class="gi"', 'class="gi" style="width:40px;height:40px"')}</span>`)
    + `<div class="memo-box" id="memo-text">${esc(a.text) || "<span style='color:var(--fg-3)'>ยังไม่มีข้อความ</span>"}</div>`
    + datesHTML(a)
    + `<div class="row"><button class="btn ghost" id="memo-edit">${ic("pencil")}แก้ไข</button><button class="btn primary" id="memo-copy">${ic("copy")}คัดลอก</button></div>`,
  sh => {
    sh.querySelector("#memo-copy")!.addEventListener("click", () => copyText(a.text, sh.querySelector("#memo-text")));
    sh.querySelector("#memo-edit")!.addEventListener("click", () => openEditor("act", a));
  });
}

const shareText = (a: Action) => `${a.name}\n${a.url}`;
function openShare(a: Action) {
  if (!a.url) {
    openSheet(head("แชร์ " + a.name)
      + `<p class="note">ยังไม่มี URL ให้แชร์ เพิ่มลิงก์ก่อน แล้วจึงแชร์ได้</p>`
      + `<button class="btn primary" id="sh-add" style="width:100%">${ic("link")}เพิ่มลิงก์</button>`,
      sh => { sh.querySelector<HTMLElement>("#sh-add")!.onclick = () => openNoUrl(a); });
    return;
  }
  const data = { title: a.name, text: a.name, url: a.url };
  const fallback = () => openSheet(head("แชร์ " + a.name)
      + `<p class="note">เครื่องนี้หรือหน้านี้เปิดเมนูแชร์ของระบบไม่ได้ ใช้การคัดลอกแทน</p>`
      + `<div class="memo-box" id="sh-text">${esc(a.name)}\n<span class="url">${esc(a.url)}</span></div>`
      + `<div class="stack"><button class="btn primary" id="sh-copy-all">${ic("copy")}คัดลอกชื่อและลิงก์</button><button class="btn" id="sh-copy-url">${ic("link")}คัดลอกเฉพาะลิงก์</button></div>`,
      sh => {
        sh.querySelector<HTMLElement>("#sh-copy-all")!.onclick = () => copyText(shareText(a), sh.querySelector("#sh-text"));
        sh.querySelector<HTMLElement>("#sh-copy-url")!.onclick = () => copyText(a.url, sh.querySelector("#sh-text .url"));
      });
  let can = false; try { can = !!navigator.share && (!navigator.canShare || navigator.canShare(data)); } catch { /* unsupported */ }
  if (!can) return fallback();
  navigator.share(data).then(() => toast("แชร์แล้ว"), err => { if (err && err.name === "AbortError") return; fallback(); });
}

function openNoUrl(a: Action) {
  openSheet(head(a.name)
    + `<p class="note">${esc(a.name)} ยังไม่มีลิงก์ วางลิงก์ด้านล่างแล้วแตะเปิดได้ทันที</p>`
    + `<div class="field"><label for="nu-url">ลิงก์ปลายทาง</label><input class="inp" id="nu-url" inputmode="url" placeholder="https://..." autocomplete="off"><span class="err" id="nu-err" hidden>ลิงก์ไม่ถูกต้อง ต้องเป็น http:// หรือ https://</span></div>`
    + saveErrHTML("nu-save-err")
    + `<button class="btn primary" id="nu-save" style="width:100%">${ic("check")}บันทึกลิงก์</button>`,
  sh => {
    const btn = sh.querySelector<HTMLElement>("#nu-save")!, saveErr = sh.querySelector<HTMLElement>("#nu-save-err")!;
    btn.addEventListener("click", () => guarded(btn, async () => {
      saveErr.hidden = true;
      const n = normalizeUrl(sh.querySelector<HTMLInputElement>("#nu-url")!.value);
      if (!n.ok || !n.url) { sh.querySelector<HTMLElement>("#nu-err")!.hidden = false; return; }
      const r = await save(l => L.setActionUrl(l, a.id, n.url));
      if (!r.ok) { showFail(saveErr, r); return; }
      render(); toast("บันทึกลิงก์แล้ว แตะไอคอนเพื่อเปิด"); closeSheet();
    }));
  });
}

function openDetails(kind: string, id: string) {
  if (kind === "cat") {
    const c = L.getCategory(lib(), id); if (!c) return;
    const s = L.getWorkspace(lib(), c.workspaceId)!;
    openSheet(head(c.name) + `<p class="note">${esc(s.name)} › หมวด · ${L.actionsOf(lib(), c.id).length} Action</p>` + datesHTML(c)
      + `<div class="row"><button class="btn ghost" id="dt-edit">${ic("pencil")}แก้ไข</button><button class="btn primary" id="dt-open">เปิดหมวด</button></div>`,
      sh => { sh.querySelector<HTMLElement>("#dt-edit")!.onclick = () => openEditor("cat", c);
              sh.querySelector<HTMLElement>("#dt-open")!.onclick = () => { closeSheet(); V.view = "cat"; V.catId = c.id; render(); }; });
  } else {
    const r = findAction(id); if (!r) return; const { s, c, a } = r;
    const body = a.type === "link"
      ? (a.url ? `<div class="field"><span class="lab">ลิงก์</span><span class="url">${esc(a.url)}</span></div>` : `<p class="note">ยังไม่มีลิงก์</p>`)
      : `<div class="memo-box" style="max-height:140px;overflow:auto">${esc(a.text)}</div>`;
    openSheet(head(a.name) + `<p class="note">${esc(s.name)} › ${esc(c.name)} · ${a.type === "link" ? "ลิงก์" : "Memo"}</p>` + body + datesHTML(a)
      + `<div class="row"><button class="btn ghost" id="dt-edit">${ic("pencil")}แก้ไข</button>${a.type === "link" ? `<button class="btn" id="dt-share">${ic("share")}แชร์</button>` : ""}${a.type === "link" && a.url ? `<a class="btn primary" href="${esc(a.url)}" target="_blank" rel="noopener noreferrer">${ic("ext")}เปิด</a>` : ""}</div>`,
      sh => { sh.querySelector<HTMLElement>("#dt-edit")!.onclick = () => openEditor("act", a);
              const b = sh.querySelector<HTMLElement>("#dt-share"); if (b) b.onclick = () => openShare(a); });
  }
}

function getRecIn(l: typeof ctx.store.lib, kind: Kind, id: string) {
  return kind === "space" ? L.getWorkspace(l, id) : kind === "cat" ? L.getCategory(l, id) : L.getAction(l, id);
}

/* ---------- events ---------- */
export function bindEvents() {
  let pressT: ReturnType<typeof setTimeout> | undefined, pressFired = false;
  document.addEventListener("pointerdown", e => {
    const el = (e.target as Element).closest<HTMLElement>("[data-press]"); pressFired = false;
    if (!el || V.edit) return;
    pressT = setTimeout(() => { pressFired = true; if (navigator.vibrate) try { navigator.vibrate(10); } catch { /* ignore */ }
      openDetails(el.dataset.press!, el.dataset.id!); }, 520);
  });
  ["pointerup", "pointercancel", "pointerleave", "scroll"].forEach(t => document.addEventListener(t, () => clearTimeout(pressT), true));
  document.addEventListener("contextmenu", e => { if ((e.target as Element).closest("[data-press]")) e.preventDefault(); });

  document.addEventListener("click", async e => {
    const el = (e.target as Element).closest<HTMLElement>("[data-do]"); if (!el) return;
    if (pressFired && el.dataset.press) { e.preventDefault(); pressFired = false; return; }
    const act = el.dataset.do!, id = el.dataset.id!;
    if (handleWelcomeAction(act, el) || handleSettingsAction(act, el) || handleAccountAction(act, el)) return;
    switch (act) {
      case "scrim": if (e.target === el) closeSheet(); break;
      case "close": closeSheet(); break;
      case "toggle-edit": V.edit = !V.edit; render(); break;
      case "grid-toggle":
        try {
          if (V.view === "home") { folders3 = !folders3; localStorage.setItem(FOLDER_COLS_KEY, folders3 ? "3" : "2"); }
          else { dense = !dense; localStorage.setItem(DENSE_KEY, dense ? "1" : "0"); }
        } catch { /* only this session */ }
        render(); break;
      case "settings": V.view = "settings"; V.edit = false; render(); scrollTo(0, 0); break;
      case "back": V.view = "home"; V.catId = null; render(); scrollTo(0, 0); break;
      case "tab": {
        const sp = space();
        if (V.edit && sp && id === sp.id) { openEditor("space", sp); break; }
        const r = await save(l => L.setActiveWorkspace(l, id));
        if (!r.ok) { failNotice(r); break; }
        render(); break; }
      case "new-space": openEditor("space"); break;
      case "new-cat": openEditor("cat"); break;
      case "new-act": openEditor("act"); break;
      case "cat": {
        const c = L.getCategory(lib(), id); if (!c) break;
        if (V.edit) openEditor("cat", c); else { V.view = "cat"; V.catId = id; render(); scrollTo(0, 0); }
        break; }
      case "open-link": toast("เปิดลิงก์ในแท็บใหม่"); break; /* the <a> itself opens the destination */
      case "share": { e.preventDefault(); e.stopPropagation(); const r = findAction(id); if (r) openShare(r.a); break; }
      case "act": {
        const a = L.getAction(lib(), id); if (!a) break;
        if (V.edit) openEditor("act", a);
        else if (a.type === "memo") openMemo(a);
        else openNoUrl(a);
        break; }
    }
  });
  document.addEventListener("keydown", e => { if (e.key === "Escape") closeSheet(); });
}
